'use client'

import { getSanitizedPlainText } from '@/lib/utils/text-utils'
import type { ProductRichDescription_ProductFragment } from '@gql-generated/graphql'
import { useProductInfos } from '../../hooks/useProductInfos'
import {
  DESCRIPTION_ANCHOR_ID,
  EXTENDED_CONTENT_REGEX,
  EXTENDED_SANITIZE_OPTIONS,
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  SHORT_DESCRIPTION_SANITIZE_OPTIONS,
} from '../../services/product.constants'
import { ProductDetailsInfosSection } from '../product-details-section/ProductDetailsInfosSection.client'
import { ProductNorms } from '../product-norms/ProductNorms.client'
import { ProductDescription } from './ProductDescription.client'

type ProductRichDescriptionClientProps = {
  product: ProductRichDescription_ProductFragment
}

export function ProductRichDescriptionClient({ product }: ProductRichDescriptionClientProps) {
  const { activeProduct } = useProductInfos({ product })

  const currentProduct = activeProduct ?? product
  const description = currentProduct.description ?? ''

  if (!description) {
    return null
  }

  const norms = currentProduct.norms ?? []

  // EXTENDED for equality: description may contain tables/iframes absent from shortDesc
  const descPlainTextFull = getSanitizedPlainText(description, EXTENDED_SANITIZE_OPTIONS)
  // SHORT for length threshold: consistent with what is displayed in the top section
  const descPlainTextShort = getSanitizedPlainText(description, SHORT_DESCRIPTION_SANITIZE_OPTIONS)
  const shortDescPlainText = getSanitizedPlainText(
    currentProduct.shortDescription,
    SHORT_DESCRIPTION_SANITIZE_OPTIONS,
  )
  const hasShortDescription = !!shortDescPlainText && shortDescPlainText !== descPlainTextFull
  const hasNormsWithImages = norms.some((norm) => !!norm.image)
  const needsAnchorSection = descPlainTextShort.length > PRODUCT_DESCRIPTION_MAX_LENGTH
  const descHasRichContent = EXTENDED_CONTENT_REGEX.test(description)

  if (!hasShortDescription && !needsAnchorSection && !hasNormsWithImages && !descHasRichContent)
    return null

  return (
    <ProductDetailsInfosSection withDivider={false} id={DESCRIPTION_ANCHOR_ID}>
      <ProductDescription html={description} full />
      <ProductNorms norms={norms} />
    </ProductDetailsInfosSection>
  )
}
